import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, MessageCircle, Share2, Sparkles, Send, Trash2, Music } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MediaAttachmentBar from "../components/MediaAttachmentBar";

/**
 * Timeline page — every user gets a personal wall. Owners can compose; everyone can read
 * public timelines. Posts are auto cross-posted to the Community Wall by default.
 */
export default function Timeline() {
  const { user } = useAuth();
  const { userId } = useParams();
  const isMine = !userId || userId === user?.user_id;
  const [owner, setOwner] = useState(null);
  const [posts, setPosts] = useState([]);
  const [composer, setComposer] = useState({ content: "", cross_post_community: true, is_public: true, image_url: null, video_url: null, music: null });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const endpoint = isMine ? "/timeline/me" : `/timeline/${userId}`;
      const { data } = await api.get(endpoint);
      setPosts(data || []);
      if (!isMine) {
        // fetch user summary for header
        try {
          const u = await api.get(`/users/by-handle/${userId}`).catch(() => null);
          if (u) setOwner(u.data);
        } catch { /* ignore */ }
      } else {
        setOwner(user);
      }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, user?.user_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!composer.content.trim() && !composer.image_url && !composer.video_url) return;
    setBusy(true);
    try {
      await api.post("/timeline/post", composer);
      setComposer({ content: "", cross_post_community: true, is_public: true, image_url: null, video_url: null, music: null });
      toast.success(composer.cross_post_community ? "Posted to your timeline + community wall" : "Posted to your timeline");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const like = async (p) => {
    try {
      await api.post(`/timeline/${p.id}/like`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (p) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/timeline/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="timeline-page" className="pt-32 pb-24 px-4 sm:px-8 md:px-12 max-w-3xl mx-auto">
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Timeline</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl text-white mt-3 tracking-tighter">
          {isMine ? "Your wall" : `${owner?.name || "Their"} wall`}
        </h1>
        {owner?.handle && <div className="text-white/60 mt-1">@{owner.handle}</div>}
      </div>

      {isMine && user && (
        <form onSubmit={submit} data-testid="timeline-composer" className="tv-glass rounded-2xl border border-white/10 p-5 mt-8">
          <textarea
            data-testid="timeline-composer-input"
            value={composer.content}
            onChange={(e) => setComposer({ ...composer, content: e.target.value })}
            rows={3}
            maxLength={2000}
            placeholder="What's happening? Tell the Tap In crew…"
            className="w-full bg-transparent outline-none text-white placeholder:text-white/40 text-sm resize-none"
          />
          <MediaAttachmentBar value={composer} onChange={(patch) => setComposer({ ...composer, ...patch })} />
          <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-white/10 mt-2">
            <label className="flex items-center gap-2 text-xs text-white/70">
              <input type="checkbox" data-testid="timeline-crosspost-toggle" checked={composer.cross_post_community} onChange={(e) => setComposer({ ...composer, cross_post_community: e.target.checked })} />
              Also post to Community Wall
            </label>
            <button
              disabled={busy || (!composer.content.trim() && !composer.image_url && !composer.video_url)}
              data-testid="timeline-submit-btn"
              className="bg-tv-accent hover:bg-yellow-400 disabled:opacity-40 text-black font-bold px-5 py-2 rounded-full text-sm flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" /> {busy ? "Posting…" : "Post"}
            </button>
          </div>
        </form>
      )}

      <div className="mt-8 space-y-4">
        {posts.length === 0 && (
          <div className="tv-glass rounded-xl border border-white/10 p-8 text-center text-white/60">
            {isMine ? "Nothing here yet — share something with the crew." : "No posts yet."}
          </div>
        )}
        {posts.map((p) => (
          <article key={p.id} data-testid={`timeline-post-${p.id}`} className="tv-glass rounded-xl border border-white/10 p-5">
            <div className="flex items-center gap-3">
              <img src={p.user_avatar || `https://api.dicebear.com/9.x/initials/svg?seed=${p.user_name}`} alt="" className="w-10 h-10 rounded-full" />
              <div>
                <div className="text-white font-semibold text-sm">{p.user_name}</div>
                {p.user_handle && <div className="text-white/40 text-xs">@{p.user_handle}</div>}
              </div>
              <div className="flex-1" />
              {isMine && (
                <button onClick={() => del(p)} data-testid={`timeline-delete-${p.id}`} className="text-white/40 hover:text-red-400 text-xs">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="text-white/90 mt-3 whitespace-pre-wrap text-[15px] leading-relaxed">{p.content}</div>
            {p.image_url && <img src={p.image_url} alt="" className="mt-3 rounded-lg max-h-96 w-full object-cover" />}
            <div className="mt-4 flex items-center gap-5 text-white/60 text-xs">
              <button onClick={() => like(p)} data-testid={`timeline-like-${p.id}`} className="flex items-center gap-1 hover:text-tv-accent">
                <Heart className="w-4 h-4" /> {p.likes || 0}
              </button>
              <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> {p.comments_count || 0}</span>
              {p.cross_posted_community_id && (
                <Link to="/community" className="flex items-center gap-1 text-tv-brand hover:underline">
                  <Share2 className="w-3 h-3" /> Cross-posted to community
                </Link>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
